import type { ConsumptionChartModel } from "../components/consumption-chart/chartModel";
import { getBucketLabels } from "../components/consumption-chart/chartModel";
import {
	GAS_CONSUMPTION_CHART_METRIC,
	type GasConsumptionChartMetric,
	type GasConsumptionMainBucket,
	type GasConsumptionMiniBucket,
	type GasConsumptionZone,
} from "../models/GasConsumption";
import type {
	UtilityConsumptionMeter,
	UtilityConsumptionMiniBucket,
	UtilityConsumptionMode,
} from "../models/UtilityConsumption";
import { formatVolume, GAS_RATIO_UNIT, GAS_UNIT, NO_DATA_TEXT } from "./format";

const MILL_PALETTES = [
	["#0D47A1", "#42A5F5", "#1E88E5", "#90CAF9", "#64B5F6"],
	["#BF360C", "#FFB74D", "#F57C00", "#FFCC80", "#FF8A65"],
];

export const GAS_ZONE_TREE_LABELS = { items: "Zones", group: "mill" };

export const getGasZoneLabel = (
	zone: Pick<GasConsumptionZone, "IsTotal" | "Zone">,
) => (zone.IsTotal ? "Furnace total" : zone.Zone);

export const toZoneMeters = (
	zones: GasConsumptionZone[],
): UtilityConsumptionMeter[] => {
	const millIds = [...new Set(zones.map((zone) => zone.MillAssetId))];
	return zones.map((zone) => {
		const palette = MILL_PALETTES[millIds.indexOf(zone.MillAssetId) % 2];
		const millZones = zones.filter(
			(other) => other.MillAssetId === zone.MillAssetId,
		);
		return {
			ExternalMeterId: zone.ZoneKey,
			MeterSerial: null,
			AssetId: zone.MillAssetId,
			MillName: zone.MillName,
			WaterAccountId: zone.MillAssetId,
			Color: palette[millZones.indexOf(zone) % palette.length],
			AccountNumber: zone.MillName,
			AccountName: null,
			SharePercent: 100,
		};
	});
};

export const resolveGasZoneSelection = (
	zones: GasConsumptionZone[] | undefined,
	zoneKeys: string[] | null,
) => {
	const configuredKeys = zones?.map((zone) => zone.ZoneKey) ?? [];
	const requested = new Set(zoneKeys ?? []);
	const selectedKeys =
		zoneKeys === null
			? null
			: configuredKeys.filter((zoneKey) => requested.has(zoneKey));
	const isAll =
		selectedKeys === null || selectedKeys.length === configuredKeys.length;

	return {
		treeSelectedKeys: selectedKeys,
		requestZoneKeys: isAll ? null : selectedKeys,
		isEmptySelection:
			zones !== undefined && selectedKeys !== null && selectedKeys.length === 0,
		toUrlValue: (treeValue: string[] | null) => treeValue,
	};
};

export const toUtilityMiniBuckets = (
	buckets: GasConsumptionMiniBucket[],
): UtilityConsumptionMiniBucket[] =>
	buckets.map((bucket) => ({
		CurrentBucketStartUtc: bucket.CurrentBucketStartUtc,
		CurrentGallons: bucket.CurrentMmbtu,
		PreviousBucketStartUtc: bucket.PreviousBucketStartUtc,
		PreviousGallons: bucket.PreviousMmbtu,
	}));

interface BuildGasChartModelInput {
	buckets: GasConsumptionMainBucket[];
	zones: GasConsumptionZone[];
	mode: UtilityConsumptionMode;
	metric: GasConsumptionChartMetric;
}

export const buildGasChartModel = ({
	buckets,
	zones,
	mode,
	metric,
}: BuildGasChartModelInput): ConsumptionChartModel => {
	const isRatio = metric === GAS_CONSUMPTION_CHART_METRIC.MMBTU_PER_TON;
	const starts = [
		...new Map(
			buckets.map((bucket) => [
				bucket.BucketStartUtc.toISOString(),
				bucket.BucketStartUtc,
			]),
		).values(),
	];
	const bucketZoneKeys = new Set(buckets.map((bucket) => bucket.ZoneKey));
	const chartZones = zones.filter((zone) => bucketZoneKeys.has(zone.ZoneKey));
	const meterIds = chartZones.map((zone) => zone.ZoneKey);
	const zoneByKey = new Map(chartZones.map((zone) => [zone.ZoneKey, zone]));
	const colorByKey = new Map(
		toZoneMeters(zones).map((meter) => [meter.ExternalMeterId, meter.Color]),
	);
	const millIds = [...new Set(chartZones.map((zone) => zone.MillAssetId))];

	const bucketByStartAndZone = new Map(
		buckets.map((bucket) => [
			`${bucket.BucketStartUtc.toISOString()}|${bucket.ZoneKey}`,
			bucket,
		]),
	);
	const valueFor = (start: Date, zoneKey: string) => {
		const bucket = bucketByStartAndZone.get(
			`${start.toISOString()}|${zoneKey}`,
		);
		if (bucket === undefined) return null;
		return isRatio ? bucket.MmbtuPerTon : bucket.Mmbtu;
	};

	const hasOverlap = millIds.some(
		(millId) =>
			chartZones.some((zone) => zone.MillAssetId === millId && zone.IsTotal) &&
			chartZones.some((zone) => zone.MillAssetId === millId && !zone.IsTotal),
	);

	const bucketFigure = (start: Date) => {
		if (isRatio && millIds.length > 1) return null;
		const millValues = millIds.map((millId) => {
			const millZones = chartZones.filter(
				(zone) => zone.MillAssetId === millId,
			);
			const total = millZones.find((zone) => zone.IsTotal);
			const values = (total === undefined ? millZones : [total])
				.map((zone) => valueFor(start, zone.ZoneKey))
				.filter((value): value is number => value !== null);
			return values.length === 0
				? null
				: values.reduce((sum, value) => sum + value, 0);
		});
		const known = millValues.filter((value): value is number => value !== null);
		return known.length === 0
			? null
			: known.reduce((sum, value) => sum + value, 0);
	};

	const labels = getBucketLabels(starts, mode);
	const getMeterName = (zoneKey: string) => {
		const zone = zoneByKey.get(zoneKey);
		return zone === undefined ? zoneKey : getGasZoneLabel(zone);
	};

	return {
		labels,
		meterIds,
		unit: isRatio ? GAS_RATIO_UNIT : GAS_UNIT,
		axisName: isRatio ? "MMBtu per ton" : "MMBtu",
		valuesByMeterId: new Map(
			meterIds.map((zoneKey) => [
				zoneKey,
				starts.map((start) => valueFor(start, zoneKey)),
			]),
		),
		showAccountNumbers: millIds.length > 1,
		accountNumberByMeterId: new Map(
			chartZones.map((zone) => [zone.ZoneKey, zone.MillName]),
		),
		getMeterName,
		getMeterColor: (zoneKey: string) => colorByKey.get(zoneKey) ?? "#8C9AA5",
		getSeriesName: (zoneKey: string) => zoneKey,
		meterIdBySeriesName: new Map(meterIds.map((zoneKey) => [zoneKey, zoneKey])),
		detailRows: starts.map((start, index) => ({
			id: start.toISOString(),
			period: labels[index] ?? NO_DATA_TEXT,
			value: formatVolume(bucketFigure(start)) ?? NO_DATA_TEXT,
		})),
		bucketIndexByRowId: new Map(
			starts.map((start, index) => [start.toISOString(), index]),
		),
		groupLabel: "",
		getStackKey: (zoneKey: string) => {
			const zone = zoneByKey.get(zoneKey);
			return zone === undefined
				? zoneKey
				: `${zone.MillAssetId}-${zone.IsTotal ? "total" : "zones"}`;
		},
		showTooltipTotal: !hasOverlap && !(isRatio && millIds.length > 1),
	};
};
